import buffer from '@turf/buffer';
import { polygon } from '@turf/helpers';
import type { Feature, MultiPolygon, Polygon } from 'geojson';
import type { LatLngPoint, ZonePolygons } from '@/shared/types';

function toTurfPolygon(points: LatLngPoint[]) {
  const coords = points.map(p => [p.lng, p.lat]);
  // turf requires a closed ring
  coords.push(coords[0]);
  return polygon([coords]);
}

function fromTurfCoords(coords: number[][]): LatLngPoint[] {
  // drop the closing duplicate point
  return coords.slice(0, -1).map(([lng, lat]) => ({ lat, lng }));
}

export interface ZoneOptions {
  bufferMeters?: number;
  terminationMeters?: number;
}

export function computeZones(
  flightArea: LatLngPoint[],
  options: ZoneOptions = {},
): ZonePolygons {
  if (flightArea.length < 3) {
    return { flightArea: [], buffer: [], termination: [] };
  }

  let current: Feature<Polygon | MultiPolygon> = toTurfPolygon(flightArea);
  let bufferPoints: LatLngPoint[] = [];
  if (options.bufferMeters) {
    const bufferFeature = buffer(current, options.bufferMeters, { units: 'meters' })!;
    bufferPoints = fromTurfCoords(bufferFeature.geometry.coordinates[0] as number[][]);
    current = bufferFeature;
  }

  let terminationPoints: LatLngPoint[] = [];
  if (options.terminationMeters) {
    const terminationFeature = buffer(current, options.terminationMeters, { units: 'meters' })!;
    terminationPoints = fromTurfCoords(terminationFeature.geometry.coordinates[0] as number[][]);
  }

  return { flightArea, buffer: bufferPoints, termination: terminationPoints };
}

const EARTH_RADIUS_M = 6371000;

function haversineMeters(a: LatLngPoint, b: LatLngPoint): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Mirrors the backend's FlightNotification.farthest_point_distance_m(): for every
// flight-area vertex, the distance to the nearest takeoff point, maxed over vertices.
export function farthestFlightAreaDistance(
  flightArea: LatLngPoint[],
  takeoffPoints: LatLngPoint[],
): number {
  if (flightArea.length < 3 || takeoffPoints.length === 0) return 0;
  let max = 0;
  for (const vertex of flightArea) {
    const nearest = Math.min(...takeoffPoints.map(tp => haversineMeters(tp, vertex)));
    max = Math.max(max, nearest);
  }
  return max;
}

// Boundary points may return true or false; acceptable for takeoff-inside-polygon checks.
export function isPointInPolygon(point: LatLngPoint, polygon: LatLngPoint[]): boolean {
  let inside = false;
  const { lat: py, lng: px } = point;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const { lat: iy, lng: ix } = polygon[i];
    const { lat: jy, lng: jx } = polygon[j];
    const intersect = ((iy > py) !== (jy > py)) && (px < (jx - ix) * (py - iy) / (jy - iy) + ix);
    if (intersect) inside = !inside;
  }
  return inside;
}
